import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MagnifyingGlassIcon, XMarkIcon, ChevronDownIcon, UserIcon } from '@heroicons/react/24/outline';

interface HeaderProps {
  isTransparent?: boolean;
}

const Header: React.FC<HeaderProps> = ({ isTransparent = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const [expandedMobileMenu, setExpandedMobileMenu] = useState<number | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.user-dropdown-container')) {
        setIsUserDropdownOpen(false);
      }
    };

    if (isUserDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserDropdownOpen]);

  // Close mobile menu and reset expanded state when menu closes
  useEffect(() => {
    if (!isMenuOpen) {
      setExpandedMobileMenu(null);
    }
  }, [isMenuOpen]);

  // Scroll detection for transparent header
  useEffect(() => {
    if (!isTransparent) return;

    const handleScroll = () => {
      // 배너 높이의 80% 지점에서 헤더 색상 변경
      const scrollThreshold = window.innerHeight * 0.8;
      setIsScrolled(window.scrollY > scrollThreshold);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // 초기 상태 설정

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isTransparent]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchKeyword.trim()) {
      console.log('Searching for:', searchKeyword);
      setSearchKeyword('');
      setIsSearchOpen(false);
    }
  };

  const menuItems = [
    {
      id: 1,
      title: '센터소개',
      path: '/about/greeting',
      subItems: [
        { title: '인사말', path: '/about/greeting' },
        { title: 'DGTC소개', path: '/about/gtc' },
        { title: '교육과정', path: '/curriculum' },
        { title: '시설안내', path: '/about/facilities' },
        { title: '오시는 길', path: '/about/location' }
      ]
    },
    {
      id: 2,
      title: '프로그램',
      path: '/programs',
      subItems: [
        { title: '단체체험', path: '/programs/category/group' },
        { title: '유치과정', path: '/programs/category/preschool' },
        { title: '초등과정', path: '/programs/category/elementary' },
        { title: '중·고등과정', path: '/programs/category/secondary' },
        { title: '성인과정', path: '/programs/category/adult' },
        { title: '특별과정', path: '/programs/category/special' },
        { title: '방학캠프', path: '/programs/category/camp' },
        { title: '국제교류', path: '/programs/category/international' }
      ]
    },
    {
      id: 3,
      title: '공지사항',
      path: '/notice',
      subItems: [
        { title: '공지사항', path: '/notice' },
        { title: '교육 자료실', path: '/notice/materials' }
      ]
    },
    {
      id: 4,
      title: '수강신청',
      path: '/application',
      subItems: [
        { title: '레벨 테스트 신청', path: '/application/level-test' },
        { title: '수강신청', path: '/application/register' },
        { title: '단체체험(기관) 신청', path: '/application/group' }
      ]
    },
    {
      id: 5,
      title: '참여마당',
      path: '/community',
      subItems: [
        { title: '자주 묻는 질문', path: '/community/faq' },
        { title: '갤러리', path: '/gallery' },
        { title: '수강후기', path: '/community/reviews' },
        { title: '설문조사', path: '/community/survey' }
      ]
    }
  ];

  return (
    <>
      <header
        className={`header fixed w-full top-0 z-[100] transition-colors duration-300 ${
          isTransparent && !isHeaderHovered && !isScrolled
            ? 'bg-transparent'
            : 'bg-white border-b border-gray-200'
        }`}
        onMouseEnter={() => setIsHeaderHovered(true)}
        onMouseLeave={() => setIsHeaderHovered(false)}
      >
        <div className="header__inner w-full relative">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex justify-between items-center h-20">
              {/* Logo */}
              <Link
                to="/"
                className="header__logo absolute left-4 md:left-6 2xl:left-8 top-5 flex items-center gap-2 group z-[110]"
              >
                <img
                  src="/logo.png"
                  alt="DDGTC Logo"
                  className="h-10 w-auto transition-transform duration-300 group-hover:scale-105"
                />
                <div className="flex flex-col">
                  <span className={`text-xs font-bold tracking-wide transition-colors duration-300 whitespace-nowrap ${
                    isTransparent && !isHeaderHovered && !isScrolled ? 'text-white' : 'text-gray-800'
                  }`}>
                    DONGDUCHEON GLOBAL
                  </span>
                  <span className={`text-[10px] font-medium tracking-wide transition-colors duration-300 ${
                    isTransparent && !isHeaderHovered && !isScrolled ? 'text-gray-200' : 'text-gray-500'
                  }`}>
                    TALENT CENTER
                  </span>
                </div>
              </Link>

              {/* Right Side Icons */}
              <div className="header__link flex items-center gap-4 md:gap-5 absolute right-4 md:right-6 2xl:right-8 top-1/2 -translate-y-1/2">
              {/* Search */}
              <div className="search_box relative flex items-center">
                {!isSearchOpen ? (
                  <button
                    onClick={() => setIsSearchOpen(true)}
                    className={`transition-colors duration-300 ${
                      isTransparent && !isHeaderHovered && !isScrolled
                        ? 'text-white hover:text-blue-300'
                        : 'text-gray-600 hover:text-blue-600'
                    }`}
                  >
                    <MagnifyingGlassIcon className="h-6 w-6" />
                  </button>
                ) : (
                  <form onSubmit={handleSearch} className="absolute right-0 top-1/2 -translate-y-1/2 z-50">
                    <div className="flex items-center bg-white border border-gray-200 rounded-full px-4 py-2 shadow-lg">
                      <input
                        type="text"
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                        className="txt bg-transparent outline-none text-sm w-48 text-gray-700"
                        placeholder="검색어를 입력하세요"
                        autoFocus
                        onBlur={() => setTimeout(() => setIsSearchOpen(false), 200)}
                      />
                      <button type="submit">
                        <MagnifyingGlassIcon className="h-4 w-4 text-gray-500" />
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* User Menu - 통합된 사용자 메뉴 */}
              <div className="hidden md:flex items-center relative user-dropdown-container">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsHeaderHovered(false); // 네비게이션 드롭다운 닫기
                    setIsUserDropdownOpen(!isUserDropdownOpen);
                  }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300 ${
                    isTransparent && !isHeaderHovered && !isScrolled
                      ? 'hover:bg-white/10 text-white'
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  {/* 아바타 */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors duration-300 ${
                    isTransparent && !isHeaderHovered && !isScrolled
                      ? 'bg-white/20 text-white'
                      : 'bg-emerald-600 text-white'
                  }`}>
                    {user?.name?.charAt(0) || 'G'}
                  </div>
                  {/* 이름 */}
                  <span className="text-sm font-medium">{user?.name || '게스트'}님</span>
                  <ChevronDownIcon className="h-4 w-4" />
                </button>

                {isUserDropdownOpen && (
                  <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999]">
                    {!user ? (
                      <div className="py-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/login');
                            setIsUserDropdownOpen(false);
                          }}
                          className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          로그인
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/signup');
                            setIsUserDropdownOpen(false);
                          }}
                          className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          회원가입
                        </button>
                      </div>
                    ) : (
                      <>
                        {/* 사용자 정보 헤더 */}
                        <div className="px-4 py-3 border-b border-gray-100">
                          <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{user.email}</p>
                        </div>

                        {/* 메뉴 항목 */}
                        <div className="py-1">
                          {(user.user_type === 'adult' || user.user_type === 'guardian') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate('/mypage');
                                setIsUserDropdownOpen(false);
                              }}
                              className="flex items-center gap-2 w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                            >
                              <UserIcon className="h-4 w-4" />
                              마이페이지
                            </button>
                          )}
                          {user.user_type === 'admin' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate('/admin');
                                setIsUserDropdownOpen(false);
                              }}
                              className="flex items-center gap-2 w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              관리자 페이지
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLogout();
                              setIsUserDropdownOpen(false);
                            }}
                            className="flex items-center gap-2 w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-t border-gray-100"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            로그아웃
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                className="2xl:hidden header__btn"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                <div className="btn__wrap w-6 h-6 flex flex-col justify-center space-y-1.5">
                  {isMenuOpen ? (
                    <XMarkIcon className={`h-6 w-6 transition-colors duration-300 ${
                      isTransparent && !isHeaderHovered && !isScrolled ? 'text-white' : 'text-gray-700'
                    }`} />
                  ) : (
                    <>
                      <span className={`block w-full h-0.5 transition-colors duration-300 ${
                        isTransparent && !isHeaderHovered && !isScrolled ? 'bg-white' : 'bg-gray-700'
                      }`}></span>
                      <span className={`block w-full h-0.5 transition-colors duration-300 ${
                        isTransparent && !isHeaderHovered && !isScrolled ? 'bg-white' : 'bg-gray-700'
                      }`}></span>
                      <span className={`block w-full h-0.5 transition-colors duration-300 ${
                        isTransparent && !isHeaderHovered && !isScrolled ? 'bg-white' : 'bg-gray-700'
                      }`}></span>
                    </>
                  )}
                </div>
              </button>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden 2xl:block absolute left-1/2 transform -translate-x-1/2 top-0 h-20 pointer-events-auto z-[100]">
            <ul className="flex m-0 p-0 list-none h-20 gap-8">
              {menuItems.map((item) => (
                <li key={item.id} className="m-0 p-0 flex items-center justify-center" style={{ width: '140px' }}>
                  <Link
                    to={item.path}
                    className={`font-semibold text-[17px] transition-colors duration-300 whitespace-nowrap ${
                      isTransparent && !isHeaderHovered && !isScrolled
                        ? 'text-white hover:text-blue-300'
                        : 'text-gray-700 hover:text-blue-600'
                    }`}
                  >
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Desktop Dropdown Background - Full Width (Only for 2xl and above) */}
          <div className={`hidden 2xl:block absolute w-screen bg-white border-t border-gray-200 shadow-lg overflow-hidden z-[90] transition-all duration-300`}
            style={{
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              height: isHeaderHovered && !isUserDropdownOpen ? '450px' : '0',
              paddingTop: isHeaderHovered && !isUserDropdownOpen ? '30px' : '0',
              paddingBottom: isHeaderHovered && !isUserDropdownOpen ? '80px' : '0',
              opacity: isHeaderHovered && !isUserDropdownOpen ? 1 : 0,
              visibility: isHeaderHovered && !isUserDropdownOpen ? 'visible' : 'hidden'
            }}
          >
            <div className="absolute left-1/2 transform -translate-x-1/2 top-[30px]">
              <ul className="flex m-0 p-0 list-none gap-8">
                {menuItems.map((item) => (
                  <li key={item.id} className="m-0 p-0" style={{ width: '140px' }}>
                    <ul className="m-0 p-0 list-none">
                      {item.subItems.map((subItem) => (
                        <li key={subItem.path} className="m-0 p-0 mb-7 last:mb-0">
                          <Link
                            to={subItem.path}
                            className="block text-[16px] font-medium text-gray-600 hover:text-blue-600 transition-colors whitespace-nowrap text-center"
                            onClick={() => setIsHeaderHovered(false)}
                          >
                            {subItem.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Dropdown */}
      <div
        className={`2xl:hidden fixed left-0 right-0 bg-white border-b border-gray-200 shadow-lg overflow-hidden transition-all duration-300 z-[90]`}
        style={{
          top: '80px',
          maxHeight: isMenuOpen ? '80vh' : '0',
          opacity: isMenuOpen ? 1 : 0
        }}
      >
        <nav className="overflow-y-auto" style={{ maxHeight: '80vh' }}>
          {/* 모바일 로그인/회원가입 버튼 */}
          <div className="border-b border-gray-100 bg-gray-50">
            {!user ? (
              <div className="flex gap-2 px-6 py-4">
                <button
                  onClick={() => {
                    navigate('/login');
                    setIsMenuOpen(false);
                  }}
                  className="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
                >
                  로그인
                </button>
                <button
                  onClick={() => {
                    navigate('/signup');
                    setIsMenuOpen(false);
                  }}
                  className="flex-1 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
                >
                  회원가입
                </button>
              </div>
            ) : (
              <div className="px-6 py-4">
                <div className="text-sm text-gray-600 mb-3">{user.name}님</div>
                <div className="flex flex-col gap-2">
                  {/* 마이페이지 - 강조 */}
                  {(user.user_type === 'adult' || user.user_type === 'guardian') && (
                    <button
                      onClick={() => {
                        navigate('/mypage');
                        setIsMenuOpen(false);
                      }}
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      <UserIcon className="h-5 w-5" />
                      마이페이지
                    </button>
                  )}
                  <div className="flex gap-2">
                    {user.user_type === 'admin' && (
                      <button
                        onClick={() => {
                          navigate('/admin');
                          setIsMenuOpen(false);
                        }}
                        className="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
                      >
                        관리자
                      </button>
                    )}
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
                    >
                      로그아웃
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {menuItems.map((item) => (
            <div key={item.id} className="border-b border-gray-100">
              <button
                onClick={() => setExpandedMobileMenu(expandedMobileMenu === item.id ? null : item.id)}
                className="w-full px-6 py-4 flex items-center justify-between text-gray-700 font-medium text-base hover:bg-gray-50"
              >
                <span>{item.title}</span>
                <svg
                  className={`w-5 h-5 transition-transform duration-200 ${
                    expandedMobileMenu === item.id ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 bg-gray-50`}
                style={{
                  maxHeight: expandedMobileMenu === item.id ? `${item.subItems.length * 48}px` : '0'
                }}
              >
                {item.subItems.map((subItem) => (
                  <Link
                    key={subItem.path}
                    to={subItem.path}
                    className="block px-6 py-3 text-gray-600 text-sm hover:text-blue-600 hover:bg-gray-100"
                    onClick={() => {
                      setIsMenuOpen(false);
                      setExpandedMobileMenu(null);
                    }}
                  >
                    {subItem.title}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div
          className="2xl:hidden fixed inset-0 bg-black bg-opacity-20 z-[80]"
          style={{ top: '80px' }}
          onClick={() => {
            setIsMenuOpen(false);
            setExpandedMobileMenu(null);
          }}
        />
      )}
    </>
  );
};

export default Header;
